import React, { useEffect, useRef, useState } from 'react';
import { Alert, Modal, Button, Form } from 'react-bootstrap';
import {
  PromoType,
  VipPromoKind,
  VipTier,
  VipTierLabel,
  VipTierRungs,
} from '../../../utils/constants';
import { getCampaigns } from '../../../apis/campaigns.api';
import { UTC_INPUT_HINT, showUtcDate, utcInputToIso } from '../../../utils/utcDateTime';

// The picker's options: campaigns whose expiry is still ahead, by name. A
// campaign with no expiry counts as live. Prod carries hundreds of expired
// campaigns; hiding them keeps the list readable.
export const liveCampaigns = (list, now = Date.now()) =>
  (Array.isArray(list) ? list : [])
    .filter(c => c?.id && (!c.expireAt || Date.parse(c.expireAt) > now))
    .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));

export const claimsCapCampaigns = (list, now = Date.now()) =>
  liveCampaigns(list, now).filter(c => c.capMode === 'allocated');

export const campaignOptionLabel = c =>
  [
    c.name ?? c.id,
    c.expireAt ? `until ${showUtcDate(c.expireAt)}` : null,
    c.solLimit != null ? `cap ${c.solLimit} SOL` : null,
    c.capMode === 'allocated' ? 'counts claims' : c.capMode === 'played' ? 'counts plays' : null,
  ]
    .filter(Boolean)
    .join(' · ');

const CreatePromoModal = ({ show, onHide, onCreate }) => {
  const [promoName, setPromoName] = useState('');
  const [communityId, setCommunityId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [promoType, setPromoType] = useState(PromoType.RAFFLE);
  const [vipPromoKind, setVipPromoKind] = useState(VipPromoKind.TIER);
  const [vipTier, setVipTier] = useState(VipTier.BD);
  // Top-N place count for LEADERBOARD promos (atlantis: rankN required int ≥ 1
  // when vipPromoKind=LEADERBOARD, forbidden otherwise). Default 3 = Top-3.
  const [rankN, setRankN] = useState('3');
  const [rankNError, setRankNError] = useState('');
  const [dateError, setDateError] = useState('');
  const [createError, setCreateError] = useState(null);
  // Track C: grants use the campaign sealed on each WAVE. Atlantis create
  // may still accept promo.rfpCampaignId as a leftover default — this pick
  // is optional, labelled legacy/default, and omitted from the body when
  // blank. Forbids it on every other promo type.
  const [rfpCampaignId, setRfpCampaignId] = useState('');
  const [rfpCampaignError, setRfpCampaignError] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsState, setCampaignsState] = useState('idle'); // idle | loading | ready | error

  const isVipPromo = promoType === PromoType.VIP;
  const isWaitlist = promoType === PromoType.WAITLIST;
  const isLeaderboard = isVipPromo && vipPromoKind === VipPromoKind.LEADERBOARD;
  // Rungs come from the shared ladder (TRU-1116), so a new tier shows up in the
  // picker without touching this file. ALL is leaderboard-only.
  const vipTierOptions =
    vipPromoKind === VipPromoKind.LEADERBOARD ? [...VipTierRungs, VipTier.ALL] : VipTierRungs;

  // Load once per open (a ref, not the state, guards the request — putting the
  // state in the deps would cancel the in-flight read the moment it flipped to
  // "loading"). Retry bumps a tick to run it again. Closing the modal or
  // leaving Waitlist releases the guard, whether the read settled or not.
  const campaignsRequested = useRef(false);
  const [campaignsRetry, setCampaignsRetry] = useState(0);
  useEffect(() => {
    if (!show || !isWaitlist || campaignsRequested.current) return undefined;
    campaignsRequested.current = true;
    let cancelled = false;
    setCampaignsState('loading');
    getCampaigns()
      .then(list => {
        if (cancelled) return;
        const options = claimsCapCampaigns(list);
        setCampaigns(options);
        setRfpCampaignId(id => (options.some(c => c.id === id) ? id : ''));
        setCampaignsState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCampaigns([]);
        setRfpCampaignId('');
        setCampaignsState('error');
      });
    return () => {
      cancelled = true;
      campaignsRequested.current = false;
    };
  }, [show, isWaitlist, campaignsRetry]);
  const retryCampaigns = () => {
    campaignsRequested.current = false;
    setCampaignsRetry(t => t + 1);
  };

  const validateRfpCampaign = () => {
    setRfpCampaignError('');
    return true;
  };

  const validateRankN = () => {
    if (!isLeaderboard) {
      setRankNError('');
      return true;
    }
    const n = Number(rankN);
    if (!Number.isInteger(n) || n < 1) {
      setRankNError('Top-N must be a whole number of 1 or more.');
      return false;
    }
    setRankNError('');
    return true;
  };

  // strict = the submit: both instants are required BEFORE the ISO
  // conversion below (new Date('').toISOString() throws, and the modal
  // showed nothing). The live onChange check stays lenient while the
  // operator is still filling the first field.
  const validateDates = (strict = false) => {
    if (!startTime || !endTime) {
      setDateError(strict ? 'Set both the start time and the end time.' : '');
      return false;
    }
    if (endTime <= startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateDates(true)) return;
    if (!validateRankN()) return;
    if (!validateRfpCampaign()) return;
    setCreateError(null);
    try {
      await onCreate({
        promoName,
        communityId,
        // datetime-local carries no zone. The typed clock is the UTC instant.
        startTime: utcInputToIso(startTime),
        endTime: utcInputToIso(endTime),
        scope: 'GLOBAL',
        promoType,
        // backend requires vipTier for VIP promos and forbids it for all others;
        // rankN is required for LEADERBOARD and forbidden for TIER (400 either way)
        ...(isVipPromo && { vipPromoKind, vipTier }),
        ...(isLeaderboard && { rankN: Number(rankN) }),
        // optional leftover default while Atlantis still accepts it; waves
        // seal their own campaign at create
        ...(isWaitlist && rfpCampaignId ? { rfpCampaignId } : {}),
      });
    } catch (error) {
      const body = error?.response?.data;
      const message =
        body?.error?.message ??
        body?.message ??
        (typeof body === 'string' ? body : null) ??
        'Could not create the promo.';
      setCreateError(message);
      console.error('Error creating promo:', error);
      return;
    }
    onHide();
  };

  const handlePromoTypeChange = event => {
    const nextPromoType = event.target.value;
    setPromoType(nextPromoType);

    if (nextPromoType !== PromoType.VIP) {
      setVipPromoKind(VipPromoKind.TIER);
      setVipTier(VipTier.BD);
    }
    if (nextPromoType !== PromoType.WAITLIST) {
      setRfpCampaignId('');
      setRfpCampaignError('');
    }
  };

  const handleVipPromoKindChange = event => {
    const nextVipPromoKind = event.target.value;
    setVipPromoKind(nextVipPromoKind);
    setVipTier(nextVipPromoKind === VipPromoKind.LEADERBOARD ? VipTier.ALL : VipTier.BD);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Promo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {createError && (
          <Alert variant="danger" dismissible onClose={() => setCreateError(null)}>
            {createError}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Promo Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter promo name"
              value={promoName}
              onChange={e => setPromoName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Community</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Community Id"
              value={communityId}
              onChange={e => setCommunityId(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Start Time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={startTime}
              onChange={e => {
                setStartTime(e.target.value);
                validateDates();
              }}
            />
            <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End Time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={endTime}
              onChange={e => {
                setEndTime(e.target.value);
                validateDates();
              }}
            />
            <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
            {dateError && <div className="text-danger">{dateError}</div>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Promo Type</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="raffleRadio"
                label="Raffle"
                name="promoType"
                value={PromoType.RAFFLE}
                checked={promoType === PromoType.RAFFLE}
                onChange={handlePromoTypeChange}
              />
              <Form.Check
                type="radio"
                id="fragmentRadio"
                label="Fragment"
                name="promoType"
                value={PromoType.FRAGMENT}
                checked={promoType === PromoType.FRAGMENT}
                onChange={handlePromoTypeChange}
              />
              <Form.Check
                type="radio"
                id="loserRadio"
                label="Loser"
                name="promoType"
                value={PromoType.LOSER}
                checked={promoType === PromoType.LOSER}
                onChange={handlePromoTypeChange}
              />
              <Form.Check
                type="radio"
                id="vipRadio"
                label="VIP"
                name="promoType"
                value={PromoType.VIP}
                checked={promoType === PromoType.VIP}
                onChange={handlePromoTypeChange}
              />
              <Form.Check
                type="radio"
                id="waitlistRadio"
                label="Waitlist"
                name="promoType"
                value={PromoType.WAITLIST}
                checked={promoType === PromoType.WAITLIST}
                onChange={handlePromoTypeChange}
              />
            </div>
          </Form.Group>
          {promoType === PromoType.WAITLIST && (
            <Alert variant="secondary" className="small">
              <b>Waitlist promo.</b> These dates define the window that every wave must fit inside.
              Set each wave schedule and its prize tiers on the Waitlist tab. Prize boxes always
              win. The reward form has no odds. Boxes under this promo cannot be purchased or
              gifted.
            </Alert>
          )}
          {isWaitlist && (
            <Form.Group className="mb-3" controlId="rfpCampaignId">
              <Form.Label>RFP campaign (legacy/default, optional)</Form.Label>
              <Form.Select
                value={rfpCampaignId}
                onChange={e => {
                  setRfpCampaignId(e.target.value);
                  if (e.target.value) setRfpCampaignError('');
                }}
                disabled={campaignsState === 'loading'}
                aria-label="RFP campaign"
              >
                <option value="">
                  {campaignsState === 'loading'
                    ? 'Loading campaigns…'
                    : campaignsState === 'error'
                    ? 'Could not load campaigns'
                    : 'None — waves pick their Cap campaign at create'}
                </option>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>
                    {campaignOptionLabel(c)}
                  </option>
                ))}
              </Form.Select>
              {campaignsState === 'ready' && campaigns.length === 0 && (
                <div className="mt-1">Create a campaign with cap counts = claims first</div>
              )}
              <Form.Text muted>
                Grants use the campaign sealed on each wave at create. This pick is a temporary
                promo-level default while Atlantis still accepts promo.rfpCampaignId. Leave blank
                unless you need that leftover default.
              </Form.Text>
              {campaignsState === 'error' && (
                <div className="mt-1">
                  <Button variant="link" size="sm" className="p-0" onClick={retryCampaigns}>
                    Retry loading campaigns
                  </Button>
                </div>
              )}
              {rfpCampaignError && <div className="text-danger">{rfpCampaignError}</div>}
            </Form.Group>
          )}
          {isVipPromo && (
            <>
              <Form.Group className="mb-3" controlId="vipPromoKind">
                <Form.Label>VIP Promo Kind</Form.Label>
                <Form.Select value={vipPromoKind} onChange={handleVipPromoKindChange}>
                  <option value={VipPromoKind.TIER}>Tier</option>
                  <option value={VipPromoKind.LEADERBOARD}>Leaderboard</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="vipTier">
                <Form.Label>VIP Tier</Form.Label>
                <Form.Select value={vipTier} onChange={e => setVipTier(e.target.value)}>
                  {vipTierOptions.map(tier => (
                    <option key={tier} value={tier}>
                      {tier === VipTier.ALL ? 'All VIP Tiers' : VipTierLabel[tier] ?? tier}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              {isLeaderboard && (
                <Form.Group className="mb-3" controlId="rankN">
                  <Form.Label>Leaderboard places (Top-N)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    step={1}
                    value={rankN}
                    onChange={e => setRankN(e.target.value)}
                    onBlur={validateRankN}
                  />
                  <Form.Text muted>
                    After creating, add exactly this many prize items ranked 1..N (e.g. Top-3 →
                    items ranked 1, 2, 3). Gaps or mismatches degrade the leaderboard at seal time.
                  </Form.Text>
                  {rankNError && <div className="text-danger">{rankNError}</div>}
                </Form.Group>
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Create Promo
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreatePromoModal;
